import { useEffect, useRef, useState } from 'preact/hooks';
import type { ChatApi } from '../api';
import type { ChatLine, ConfigResponse, QuickAction } from '../types';
import { pageContext } from '../pageContext';
import { LeadForm, type LeadSubmit } from './LeadForm';

interface Props {
  config: ConfigResponse;
  api: ChatApi;
}

type Phase = 'chat' | 'lead' | 'lead-done';

export function Widget({ config, api }: Props) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>('chat');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quickActions, setQuickActions] = useState<QuickAction[]>(config.quickActions ?? []);
  const [promptLead, setPromptLead] = useState(false);

  const logRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy, phase]);

  const openPanel = async () => {
    setOpen(true);
    api.logEvent({ sessionId: sessionId ?? undefined, eventType: 'widget_opened' });
    if (!sessionId) await ensureSession();
  };

  const closePanel = () => {
    setOpen(false);
    api.logEvent({ sessionId: sessionId ?? undefined, eventType: 'widget_closed' });
  };

  const ensureSession = async (): Promise<string | null> => {
    if (sessionId) return sessionId;
    try {
      const ctx = pageContext();
      const s = await api.startSession(ctx);
      setSessionId(s.sessionId);
      setMessages([
        { role: 'assistant', text: s.welcomeMessage || config.welcomeMessage || 'Hi — how can I help?', ts: Date.now() }
      ]);
      if (s.quickActions?.length) setQuickActions(s.quickActions);
      return s.sessionId;
    } catch {
      setError("Sorry — the assistant isn't available right now.");
      return null;
    }
  };

  const sendText = async (body: string, quickActionId?: number) => {
    if (!body.trim() || busy) return;
    const sid = await ensureSession();
    if (!sid) return;

    setMessages((m) => [...m, { role: 'user', text: body, ts: Date.now() }]);
    setDraft('');
    setBusy(true);
    setError(null);
    try {
      const res = await api.sendMessage({
        sessionId: sid,
        body,
        clientMessageId: randomId(),
        quickActionId
      });
      setMessages((m) => [...m, { role: 'assistant', text: res.reply, ts: Date.now() }]);
      if (res.showLeadPrompt) {
        setPromptLead(true);
        api.logEvent({ sessionId: sid, eventType: 'lead_prompt_shown' });
      }
      if (res.quickActions?.length) setQuickActions(res.quickActions);
    } catch {
      setError("Sorry — I couldn't reach the assistant. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleQuickAction = (q: QuickAction) => {
    api.logEvent({ sessionId: sessionId ?? undefined, eventType: 'quick_action_clicked', payload: { id: q.id, label: q.label } });
    void sendText(q.payload, q.id);
  };

  const handleLeadSubmit = async (lead: LeadSubmit) => {
    const sid = await ensureSession();
    if (!sid) return;
    await api.submitLead({ sessionId: sid, ...lead });
    api.logEvent({ sessionId: sid, eventType: 'lead_submitted' });
    setPhase('lead-done');
    setPromptLead(false);
    setMessages((m) => [
      ...m,
      { role: 'assistant', text: config.leadCaptureCopy || "Thanks — we'll be in touch shortly.", ts: Date.now() }
    ]);
  };

  if (!open) {
    return (
      <button class="vc-launcher" onClick={openPanel} aria-label="Open chat">
        <span aria-hidden="true">💬</span>
        <span>{config.launcherLabel || 'Chat with us'}</span>
      </button>
    );
  }

  return (
    <div class="vc-panel" role="dialog" aria-label={`${config.displayName} chat`}>
      <div class="vc-header">
        <div class="vc-header-title">{config.displayName}</div>
        <button class="vc-close" onClick={closePanel} aria-label="Close chat">×</button>
      </div>

      <div class="vc-log" ref={logRef} aria-live="polite">
        {messages.map((m, i) => (
          <div key={i} class={`vc-msg vc-${m.role}`}>
            <div class="vc-bubble">{m.text}</div>
          </div>
        ))}
        {busy && (
          <div class="vc-msg vc-assistant vc-typing">
            <div class="vc-bubble">Thinking</div>
          </div>
        )}
      </div>

      {error && <div class="vc-error">{error}</div>}

      {phase === 'chat' && !promptLead && quickActions.length > 0 && (
        <div class="vc-quick">
          {quickActions.map((q) => (
            <button key={q.id} class="vc-chip" onClick={() => handleQuickAction(q)}>
              {q.label}
            </button>
          ))}
        </div>
      )}

      {promptLead && phase === 'chat' && (
        <LeadForm
          copy={config.leadCaptureCopy || 'Leave your details and our team will follow up shortly.'}
          onSubmit={async (l) => {
            setPhase('lead');
            await handleLeadSubmit(l);
          }}
          onCancel={() => setPromptLead(false)}
        />
      )}

      {phase !== 'lead' && (
        <form
          class="vc-input-row"
          onSubmit={(e) => {
            e.preventDefault();
            void sendText(draft);
          }}
        >
          <input
            class="vc-input"
            value={draft}
            placeholder="Type a message…"
            onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
            maxLength={2000}
            disabled={busy}
          />
          <button class="vc-send" type="submit" disabled={busy || !draft.trim()}>
            Send
          </button>
        </form>
      )}
    </div>
  );
}

function randomId(): string {
  try { return crypto.randomUUID(); } catch { return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; }
}
